
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchDevices, seedMockDevices, FetchDevicesParams } from "@/services/deviceService";
import { Device } from "@/types/device";
import { useEffect, useState } from "react";

export type { Device, InstalledApp } from "@/types/device";
export { generateMockDevices } from "@/data/mockDevices";

// Constants for device query
export const DEVICES_QUERY_KEY = ['devices'];

export const useDevices = (params: FetchDevicesParams = {}) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(params.page || 1);
  const [pageSize] = useState(params.pageSize || 25);
  
  // Effect to handle storage events for cross-tab communication
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'device-cache-updated') {
        console.log('Storage change detected in another tab, refreshing devices data');
        queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    const handleCustomEvent = () => {
      console.log('Custom device-cache-updated event detected, refreshing data');
      queryClient.invalidateQueries({ queryKey: DEVICES_QUERY_KEY });
    };
    
    window.addEventListener('device-cache-updated', handleCustomEvent);
    
    const isNewTabLoad = !document.referrer.includes(window.location.hostname);
    if (isNewTabLoad) {
      console.log('New tab detected, refreshing devices data');
      queryClient.refetchQueries({ queryKey: DEVICES_QUERY_KEY });
    }
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('device-cache-updated', handleCustomEvent);
    };
  }, [queryClient]);

  const query = useQuery({
    queryKey: [...DEVICES_QUERY_KEY, page, pageSize],
    queryFn: () => fetchDevices({ page, pageSize }),
    staleTime: 1000 * 60 * 5, // 5 minutes - Phase 4: caching
    refetchOnMount: false, // Phase 4: reduce unnecessary refetches
  });

  return {
    ...query,
    data: query.data?.devices || [],
    total: query.data?.total || 0,
    page,
    pageSize,
    totalPages: query.data?.totalPages || 0,
    deviceTags: query.data?.deviceTags || new Map(),
    statusCounts: query.data?.statusCounts || { active: 0, inactive: 0, pending: 0, total: 0 },
    setPage,
  };
};

// Helper function to get tenant information for devices
export const getTenantInfo = (deviceId: string) => {
  const tenants = [
    { customer: "Acme Inc", tenantId: "ACM-001" },
    { customer: "Global Tech", tenantId: "GLB-002" },
    { customer: "Stark Industries", tenantId: "STK-003" },
    { customer: "Wayne Enterprises", tenantId: "WYN-004" },
    { customer: "Umbrella Corporation", tenantId: "UMB-005" },
    { customer: "Aperture Science", tenantId: "APS-006" },
    { customer: "Cyberdyne Systems", tenantId: "CYB-007" },
    { customer: "Massive Dynamic", tenantId: "MAS-008" },
    { customer: "Nakatomi Trading", tenantId: "NAK-009" },
    { customer: "Initech Software", tenantId: "INT-010" }
  ];
  
  // Assign tenant based on device type
  if (deviceId.startsWith('mac-')) {
    return tenants[0]; // Macbooks get Acme Inc
  } else if (deviceId.startsWith('iphone-')) {
    return tenants[1]; // iPhones get Global Tech
  } else if (deviceId.startsWith('ipad-')) {
    return tenants[2]; // iPads get Stark Industries
  } else if (deviceId.startsWith('apple-watch-')) {
    return tenants[3]; // Apple Watches get Wayne Enterprises
  } else if (deviceId.startsWith('apple-tv-')) {
    return tenants[4]; // Apple TVs get Umbrella Corporation
  } else if (deviceId.startsWith('vision-')) {
    return tenants[5]; // Vision Pros get Aperture Science
  } else {
    // For other devices, assign based on the last character of the ID
    const lastChar = deviceId.charAt(deviceId.length - 1);
    const index = parseInt(lastChar, 10) % tenants.length;
    return tenants[index >= 0 ? index : 0];
  }
};

// Helper to invalidate device cache
export const invalidateDevicesCache = () => {
  if (typeof window !== 'undefined') {
    const event = new Event('device-cache-updated');
    window.dispatchEvent(event);
    localStorage.setItem('device-cache-updated', new Date().getTime().toString());
    console.log('Device cache invalidated and broadcast to other tabs');
  }
};
